import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DestinationModal = ({ destination, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen || !destination) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'hotels', label: 'Hotels', icon: 'Hotel' },
    { id: 'restaurants', label: 'Restaurants', icon: 'UtensilsCrossed' },
    { id: 'transport', label: 'Transport', icon: 'Car' },
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === destination?.gallery?.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? destination?.gallery?.length - 1 : prev - 1
    );
  };

  return (
    <div
      className="fixed inset-0 z-[1040] bg-background/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen p-4 md:p-6 lg:p-8">
        <div
          className="max-w-6xl mx-auto bg-card rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-all duration-300 shadow-lg"
            >
              <Icon name="X" size={20} />
            </button>

            {/* Image Gallery */}
            <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
              <Image
                src={destination?.gallery?.[currentImageIndex]?.url}
                alt={destination?.gallery?.[currentImageIndex]?.alt}
                className="w-full h-full object-cover"
              />
              
              {/* Gallery Navigation */}
              {destination?.gallery?.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-all duration-300 shadow-lg"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center hover:bg-card transition-all duration-300 shadow-lg"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-card/90 backdrop-blur-sm rounded-full">
                    <span className="text-xs font-medium text-foreground">
                      {currentImageIndex + 1} / {destination?.gallery?.length}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Title Section */}
            <div className="mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      {destination?.name}
                    </h2>
                    {destination?.isHiddenGem && (
                      <Icon name="Sparkles" size={24} color="var(--color-accent)" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="MapPin" size={16} />
                    <span className="text-sm">{destination?.location}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="default" iconName="Heart" iconPosition="left">
                    Save
                  </Button>
                  <Button variant="outline" size="default" iconName="Share2" iconPosition="left">
                    Share
                  </Button>
                </div>
              </div>

              {/* Rating & Price */}
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <Icon name="Star" size={20} color="var(--color-accent)" />
                  <span className="text-lg font-semibold text-foreground">
                    {destination?.rating}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ({destination?.reviews} reviews)
                  </span>
                </div>
                <div className="h-6 w-px bg-border" />
                <div>
                  <p className="text-sm text-muted-foreground">Starting from</p>
                  <p className="text-2xl font-bold text-primary">
                    ₱{destination?.priceFrom?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-border mb-6">
              <div className="flex gap-2 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-300 whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span className="text-sm">{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="mb-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">About</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {destination?.fullDescription}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">Highlights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {destination?.features?.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                        >
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name={feature?.icon} size={20} color="var(--color-primary)" />
                          </div>
                          <span className="text-sm font-medium text-foreground">
                            {feature?.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'hotels' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {destination?.hotels?.map((hotel, index) => (
                    <div key={index} className="p-4 bg-muted rounded-xl">
                      <div className="flex items-start gap-3 mb-3">
                        <Image
                          src={hotel?.image}
                          alt={hotel?.imageAlt}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-1">{hotel?.name}</h4>
                          <div className="flex items-center gap-1 mb-2">
                            <Icon name="Star" size={14} color="var(--color-accent)" />
                            <span className="text-sm text-foreground">{hotel?.rating}</span>
                          </div>
                          <p className="text-sm font-semibold text-primary">
                            ₱{hotel?.price?.toLocaleString()}/night
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'restaurants' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {destination?.restaurants?.map((restaurant, index) => (
                    <div key={index} className="p-4 bg-muted rounded-xl">
                      <div className="flex items-start gap-3">
                        <Image
                          src={restaurant?.image}
                          alt={restaurant?.imageAlt}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-1">{restaurant?.name}</h4>
                          <p className="text-sm text-muted-foreground mb-2">{restaurant?.cuisine}</p>
                          <p className="text-sm font-semibold text-primary">
                            ₱{restaurant?.avgPrice?.toLocaleString()}/person
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'transport' && (
                <div className="space-y-4">
                  {destination?.transport?.map((option, index) => (
                    <div key={index} className="p-4 bg-muted rounded-xl">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={option?.icon} size={24} color="var(--color-primary)" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground mb-1">{option?.type}</h4>
                          <p className="text-sm text-muted-foreground mb-2">{option?.description}</p>
                          <p className="text-sm font-semibold text-primary">
                            ₱{option?.price?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
              >
                Book Now
              </Button>
              <Button
                variant="outline"
                size="lg"
                fullWidth
                iconName="Sparkles"
                iconPosition="left"
              >
                Generate Itinerary
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationModal;